<template>
  <div id="personal_tokens" class="proxima_nova flex flex-col gap-4">
    <div
      id="personal_tokens_header"
      class="flex flex-row justify-between items-center"
    >
      <h1 class="text-xl font-semibold text_color">Personal Tokens</h1>

      <a-button class="primary_medium" @click="handleGenerateToken">
        Generate Token
      </a-button>
    </div>

    <SimpleTable
      :headers="personalTokensTableHeaders"
      :data="tableData"
      :show-actions-column="true"
      :actions="tableActions"
    />

    <GenerateTokenModal
      :visible="showGenerateModal"
      source="personal_tokens"
      @update:visible="showGenerateModal = $event"
      @created="toListTokens"
    />

    <ConfirmationModal
      v-model:visible="showDeleteModal"
      title="Delete Token"
      :message="`Are you sure you want to delete the token '${tokenToDelete?.name}'?`"
      :loading="isDeleting"
      type="delete"
      confirm-text="Delete"
      loading-text="Deleting..."
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from "vue";
import { message } from "ant-design-vue";
import { formatDateTime } from "@/src/utils/utils.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { personalTokensTableHeaders } from "@/src/constants/codebaseConstants.js";
import codebaseService from "@/src/api/CodeService.js";
import ConfirmationModal from "@/src/components/ConfirmationModal.vue";
import SimpleTable from "@/src/components/custom_table_components/SimpleTable.vue";
import GenerateTokenModal from "@/src/components/codebase_components/tokens/GenerateTokenModal.vue";

const tokensList = ref([]);
const showGenerateModal = ref(false);
const showDeleteModal = ref(false);
const tokenToDelete = ref(null);
const isDeleting = ref(false);

const tableData = computed(() =>
  tokensList.value.map((token) => ({
    ...token,
    created_at: formatDateTime(token.created_at || null),
    expires_at: formatDateTime(token.expires_at || null),
    last_used_at: formatDateTime(token.last_used_at || null),
  })),
);

const tableActions = [
  {
    label: "Delete",
    icon: imageNames.DELETE_GREY,
    handler: (record) => handleDeleteClick(record),
  },
];

const handleGenerateToken = () => {
  showGenerateModal.value = true;
};

const handleDeleteClick = (record) => {
  try {
    tokenToDelete.value = record;
    showDeleteModal.value = true;
  } catch (error) {
    console.error("An error occurred while handling delete click: ", error);
  }
};

const toListTokens = async () => {
  try {
    const response = await codebaseService.base.tokens.list();
    if (response?.success) {
      tokensList.value = response?.data || [];
    }
  } catch (error) {
    console.error("An error occurred while listing tokens:", error);
    message.error(error?.response?.data?.error || "Failed to fetch tokens");
  }
};

const handleDeleteConfirm = async () => {
  try {
    isDeleting.value = true;
    const response = await codebaseService.base.tokens.delete(
      tokenToDelete.value?.id,
    );
    if (response?.success) {
      message.success("Token deleted successfully");
      showDeleteModal.value = false;
      tokenToDelete.value = null;
      await toListTokens();
    }
  } catch (error) {
    console.error("An error occurred while deleting token:", error);
    message.error(error?.response?.data?.error || "Failed to delete token");
  } finally {
    isDeleting.value = false;
  }
};

onMounted(() => {
  toListTokens();
});
</script>
